import {
  Caption,
  Dialog,
  FormGroup,
  FormGroupFeedback,
  Frow,
  Icon16,
  IconNames16,
  IFormGroupProps,
  Intent,
} from '@pinpointhq/thumbtack';
import axios from 'axios';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, Formik, useField } from 'formik';
import * as React from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { Editor, TrixEditor } from 'react-trix';
import Trix from 'trix';
import * as Yup from 'yup';
import { useVisibility } from '../../../hooks';
import { useFieldLoadingOverlay } from '../../../hooks/useFieldLoadingOverlay';
import { TextInsert } from '../../../TextInsert';
import { sanitize } from '../../../utilities/dompurify';
import { LoadingSpinner } from '../../spinner/LoadingSpinner';
import { createToast } from '../../toasts';
import { getCsrfToken } from '../../utilities/csrfToken';
import { RadioSelectField } from './RadioSelectField';
import { SubmitButton } from './SubmitButton';
import { TextField } from './TextField';

const handleCloudinaryImageUpload = async (file: File) => {
  axios.defaults.headers.common['x-csrf-token'] = getCsrfToken();

  const folderName = `uploads/${(window as any).railsEnv}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folderName);
  formData.append('upload_preset', 'unsigned-uploads');
  formData.append('api_key', '526742523931488');

  const res = await axios
    .post<{ secure_url: string }>('https://api.cloudinary.com/v1_1/pinpointhq/image/upload', formData)
    .catch((error) => {
      createToast({ type: 'error', text: 'Unable to upload image. Please try again.' });
      throw error;
    });

  return {
    fileName: file.name,
    url: res.data.secure_url,
  };
};

const handleAttachment = async (
  event: Event & { attachment: { file?: File; setAttributes: (...args: any[]) => void }; target: HTMLElement },
) => {
  const { attachment } = event;
  const uploadingFile = attachment.file;

  // uploading images to cloudinary
  if (uploadingFile) {
    const { url } = await handleCloudinaryImageUpload(uploadingFile);
    attachment.setAttributes({
      height: 'auto',
      href: url,
      url,
    });
  } else {
    // we must have been loading in an existing attachment
  }
};

const insertVideo = (editor: IExtendedTrixEditor, { provider, url }) => {
  const video = new Trix.Attachment({
    content: ReactDOMServer.renderToString(
      <div data-plyr-provider={provider} data-plyr-embed-id={url}>
        <iframe title="Video Player" width="560" height="315" src={url} style={{ border: 'none', width: '100%' }} />
      </div>,
    ),
  });
  editor.insertAttachment(video);
};

const trixDataAttributeRegex = /(?:data-trix-id|data-trix-store-key)="[a-zA-Z0-9/:.]+"/gm;

const createLogger =
  (namespace: string) =>
  (...messages: any[]) => {
    // leave the commented console log here please.
    // the logs are very useful and should have negligible performance impact.
    const shouldLog = false;
    // eslint-disable-next-line no-console
    if (shouldLog) console.log(`${namespace}:`, messages);
  };

const ignoreEvent = (event: Event) => {
  event.preventDefault();
};

interface IExtendedTrixEditor extends Editor {
  lastValue?: string;
  element?: HTMLElement;
  insertFile: (file: File) => void;
  insertAttachment: (attachment: unknown) => void;
  loadHTML: (html: string) => void;
}

interface ITrixFieldProps {
  allowImage?: boolean;
  allowVideo?: boolean;
  allowBase64Images?: boolean;
  characterCounterProps?: Pick<ICharacterCounter, 'displayThreshold' | 'limit'>;
  large?: boolean;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  placeholder?: string;
  allowNull?: boolean;
  variables?: { value: string; text: string; caption?: string }[];
  nameOverride?: string;
  labelInfo?: string;
}

const anyVisibleContentInField = (value: string) => {
  const sanitizedHtml = sanitize(value, {
    ALLOWED_ATTR: [],
    ALLOWED_TAGS: ['figure'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  const tempTextArea = document.createElement('textarea');
  tempTextArea.innerHTML = sanitizedHtml;
  const trimmedString = tempTextArea.value.trim();
  return trimmedString.length > 0;
};

export const buttonStyles: React.CSSProperties = {
  alignContent: 'center',
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  opacity: 0.4,
  width: '41px',
};

export function TrixField<T extends string>({
  allowImage,
  allowVideo,
  characterCounterProps,
  large,
  name,
  formGroupProps,
  placeholder,
  allowBase64Images = true,
  allowNull = false,
  variables = null,
  nameOverride,
  labelInfo,
}: ITrixFieldProps) {
  const [editor, setEditor] = React.useState<IExtendedTrixEditor>();
  const {
    isOpen: isVideoDialogOpen,
    handleClose: closeVideoDialog,
    handleOpen: openVideoDialog,
  } = useVisibility(false);
  const [{ value: fieldValue, name: fieldName }, { touched, error }, { setValue: setFieldValue }]: [
    FieldInputProps<T>,
    FieldMetaProps<T>,
    FieldHelperProps<T>,
  ] = useField<T>(name);
  const trixWrapperRef = React.useRef<HTMLDivElement>();
  // because Trix loves to run its onChange handler, we need to keep these values around
  // so that we don't get into a rerender loop.
  const lastValueReceived = React.useRef<T>();
  const lastValueSent = React.useRef<T>();
  const shouldFireUpdateRef = React.useRef<boolean>(false);
  const { loadingOverlay } = useFieldLoadingOverlay(fieldName);

  const insertString = (content: string) => {
    if (editor) editor.insertString(content);
  };

  const hasError = touched && error;

  // NOTE: this function is also called when an attachment value is loaded into the component.
  const handleAttachmentAdd = React.useMemo(
    () =>
      async (
        event: Event & { attachment: { file: File; setAttributes: (...args: any[]) => void }; target: HTMLElement },
      ) => {
        // note that this is called for each trix field on the page, so we need to make sure that the event is for us.
        if (event?.target === editor?.element) {
          handleAttachment(event);
        }
      },
    [editor],
  );

  const handleToolbarAction = React.useMemo(
    () => (event: Event & { actionName: string }) => {
      if (event.actionName === 'x-video') {
        openVideoDialog();
      } else if (event.actionName === 'x-image') {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('class', 'ephemeral-file-input');
        input.setAttribute('style', 'display: none;');
        input.setAttribute('accept', 'image/png, image/jpeg, image/gif');
        input.addEventListener('change', async (e: Event & { target?: { files?: File[] } }) => {
          const file = e.target?.files[0];
          if (file) {
            editor?.insertFile(file);
          }
        });
        // We don't _need_ to add this to the HTML, but we won't be able to test this unless we do
        document.body.appendChild(input);
        input.click();
      }
    },
    [editor, openVideoDialog],
  );

  /**
   * Called from our handleChange
   */
  const handleValue = React.useMemo(
    () => (value: T) => {
      const logger = createLogger('handleValue');
      logger(value);
      lastValueSent.current = value;
      setFieldValue(value);
    },
    [setFieldValue, lastValueSent],
  );

  /**
   * Called by Trix after any value is loaded.
   * `editor.loadHTML` will also cause this to fire.
   */
  const handleTrixChange = React.useMemo(
    () => (value: T) => {
      const logger = createLogger('handleTrixChange');

      logger('start');

      // using an IIFE (Immediately Invoked Function Expression) to wrap with logging
      (() => {
        if (typeof value !== 'string') {
          // we don't actually have a value. weird. bail out.
          logger('No string value, bailing out');
          return;
        }

        // we need to figure out if any of our values should be updated.
        if (value === lastValueSent?.current) {
          // we just sent this value to the parent, ignore it
          logger('We just sent this, bailing out');
          return;
        }

        // we must need to send a new value to our parent.
        // what is the value going to be?
        // strip out any data-trix-ids for comparisons
        const valueWithoutTrix = value.replace(trixDataAttributeRegex, '');
        const lastValueWithoutTrix = lastValueSent.current?.replace(trixDataAttributeRegex, '');

        if (valueWithoutTrix === lastValueWithoutTrix) {
          // nothing has changed except the data-trix attributes. bail out.
          logger('No change, bailing out');
          return;
        }

        if (valueWithoutTrix !== lastValueWithoutTrix) {
          // oh hello, a new value
          logger('Values differed', valueWithoutTrix, lastValueWithoutTrix);
        }

        if (value.match(`src="blob:`)) {
          // we must still be uploading something. don't update our parent state just yet.
          logger('Contained blobs, bailing out');
          return;
        }

        /**
         * NOTE (DS) Base64 image strings can be incredibly long and can cause MySQL sort memory errors. E.g sorting
         * jobs that have base64 encoded images included any of their rich text fields (such as description).
         *
         * The following types of values present in a Trix editor will not be saved if +allowBase64Images+ is false:
         *
         * 1) <a href=\"data:image/gif;base64,R0lGODlhAQABAAAAACw=\">
         *   This could occur when linking some inputted text to a base64 data uri
         *
         * 2) <img src=\"data:image/gif;base64,R0lGODlhAQABAAAAACw=\">
         *    This could occur when copy/pasting a base64 image into a Trix editor
         */
        if (!allowBase64Images) {
          const base64EncodedImageRegex: RegExp =
            /(?:href=\\"|src=\\")?data:image\/(?:gif|png|jpeg|bmp|webp|svg\+xml)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+/])+={0,2}/;

          if (value.match(base64EncodedImageRegex)) {
            logger('Contained base64 encoded image, bailing out');
            return;
          }
        }

        // now we want to check if our Trix component contained any visible values.
        if (anyVisibleContentInField(value)) {
          // great, we have a new, visible value. set it.
          handleValue(value);
          return;
        }

        /**
         * Must have been empty (or a div or block that was in essence empty)
         * Don't submit HTML if we think that there's no content in it.
         * This helps with validations on the server side. E.g. by not validating job external headers if there's empty content
         */
        handleValue('' as T);
      })();

      logger('end');
    },
    [handleValue, anyVisibleContentInField, lastValueSent, lastValueReceived],
  );

  // this is to know what value we last received from the props
  React.useEffect(() => {
    const logger = createLogger('useEffect:fieldValue');
    logger('start');
    shouldFireUpdateRef.current = false;

    if (typeof fieldValue === 'string' && !lastValueReceived.current) {
      // our component is likely rendering for the first time. (lastValueSent.current will be undefined)
      // or we might be changing languages (lastValueSent.current will be set)
      if (lastValueSent.current && fieldValue === lastValueSent.current) {
        // adding a video causes this condition
        shouldFireUpdateRef.current = false;
      } else {
        // lets cause an update.
        shouldFireUpdateRef.current = true;
        // and pretend we have already sent this value so we don't loop
        lastValueSent.current = fieldValue;
      }
    } else if (typeof fieldValue === 'string' && fieldValue === lastValueSent.current) {
      // we already sent this
      shouldFireUpdateRef.current = false;
    } else {
      // unlikely that we want to rerender our component
    }
    logger('shouldFireUpdateRef', shouldFireUpdateRef.current);
    logger('end');
  }, [fieldValue, lastValueReceived, lastValueReceived, shouldFireUpdateRef]);

  // this is to get the trix editor to reset when passed a new value that is different from the value we last used.
  React.useEffect(() => {
    const logger = createLogger('useEffect:editor,fieldValue');
    logger('start');
    // if this is running, we have received a new value prop from the above component.
    // load the content into Trix if it is different to our last 'sent' value
    if (editor && (fieldValue || allowNull) && shouldFireUpdateRef.current) {
      // get our last selected range
      const selectedRange = editor.getSelectedRange();
      // we set our editor value (which will then call handleChange).
      logger('editor.loadHTML(fieldValue)');
      editor.loadHTML(fieldValue || '');
      shouldFireUpdateRef.current = false;
      // reselect the same range. this will set focus, so only set it if we have selected anything
      if (selectedRange && selectedRange[1] && selectedRange[1] !== 0) {
        editor.setSelectedRange(selectedRange);
      }
    }
    logger('end');
  }, [editor, fieldValue, shouldFireUpdateRef]);

  // add buttons to toolbar handler
  React.useEffect(() => {
    const addButtons = (event: Event & { target?: HTMLElement & { toolbarElement?: HTMLElement } }) => {
      event.target?.toolbarElement?.querySelector('.trix-button-group--block-tools')?.insertAdjacentHTML(
        'beforeend',
        ReactDOMServer.renderToString(
          <>
            {allowImage && (
              <button type="button" className="trix-button" data-trix-action="x-image" style={buttonStyles}>
                <Icon16 icon={IconNames16.IMAGE} />
              </button>
            )}
            {allowVideo && (
              <button type="button" className="trix-button" data-trix-action="x-video" style={buttonStyles}>
                <Icon16 icon={IconNames16.VIDEO} />
              </button>
            )}
          </>,
        ),
      );
    };

    trixWrapperRef?.current?.addEventListener('trix-initialize', addButtons, { once: true });
    return () => trixWrapperRef?.current?.removeEventListener('trix-initialize', addButtons);
  }, [trixWrapperRef]);

  /**
   * Install attachment handler if we allow images, or alternatively prevent images from even being dropped
   * into the Trix editor if we don't allow images.
   *
   * @see https://github.com/basecamp/trix#storing-attached-files
   */
  React.useEffect(() => {
    if (allowImage) {
      trixWrapperRef?.current?.addEventListener('trix-attachment-add', handleAttachmentAdd);
    } else {
      trixWrapperRef?.current?.addEventListener('trix-file-accept', ignoreEvent);
    }
    return () => {
      if (allowImage) {
        trixWrapperRef?.current?.removeEventListener('trix-attachment-add', handleAttachmentAdd);
      } else {
        trixWrapperRef?.current?.removeEventListener('trix-file-accept', ignoreEvent);
      }
    };
  }, [handleAttachmentAdd, ignoreEvent]);

  // install toolbar handler
  React.useEffect(() => {
    trixWrapperRef?.current?.addEventListener('trix-action-invoke', handleToolbarAction);
    return () => trixWrapperRef?.current?.removeEventListener('trix-action-invoke', handleToolbarAction);
  }, [trixWrapperRef, handleToolbarAction]);

  return (
    <FormGroup
      {...formGroupProps}
      style={{ ...formGroupProps?.style, position: loadingOverlay ? 'relative' : null }}
      labelFor={name}
    >
      {/* If we have variables, we need to slot the 'Insert Variable' component, so need to override the default behaviour */}
      {variables && (
        <Frow justifyContent="space-between" alignContent="center" alignItems="center">
          <div>
            <label className="bp3-label mar-b-0" htmlFor={name}>
              {nameOverride || fieldName}
              {labelInfo && <span className="bp3-text-muted mar-l-1">{labelInfo}</span>}
            </label>
          </div>
          {variables && <TextInsert label="Insert Variable" items={variables} onInsert={insertString} />}
        </Frow>
      )}
      <div ref={trixWrapperRef} aria-label={fieldName}>
        <TrixEditor
          className={large ? 'trix--large' : ''}
          onChange={handleTrixChange}
          placeholder={placeholder}
          mergeTags={[]}
          onEditorReady={setEditor}
        />
        {hasError && <FormGroupFeedback intent={Intent.DANGER}>{error}</FormGroupFeedback>}
        {characterCounterProps ? (
          <CharacterCounter {...characterCounterProps} name={name} style={{ marginTop: '8px', textAlign: 'right' }} />
        ) : null}
        {fieldValue && <input type="hidden" name={fieldName} value={fieldValue} />}
      </div>
      {isVideoDialogOpen && <AddVideoDialog {...{ editor, handleClose: closeVideoDialog }} />}
      {loadingOverlay && (
        <div
          data-testid={`loadingOverlay[${fieldName}]`}
          style={{
            position: 'absolute',
            backgroundColor: '#FFFFFF80',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <LoadingSpinner height="100%" />
        </div>
      )}
    </FormGroup>
  );
}

export function AddVideoDialog({ editor, handleClose }: { editor: IExtendedTrixEditor; handleClose(): void }) {
  const handleSubmit = ({ provider, videoId }: { provider: string; videoId: string }) => {
    if (provider === 'youtube') {
      const url = `https://www.youtube.com/embed/${videoId}`;
      insertVideo(editor, { url, provider });
    } else if (provider === 'vimeo') {
      const url = `https://player.vimeo.com/video/${videoId}`;
      insertVideo(editor, { url, provider });
    }
    handleClose();
  };

  return (
    <>
      <Dialog title="Add video" isOpen={true} onClose={handleClose}>
        <Formik
          initialValues={{
            provider: '',
            videoId: '',
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            provider: Yup.string().nullable().required("Can't be blank."),
            videoId: Yup.string().nullable().required("Can't be blank."),
          })}
        >
          <>
            <TextField
              name="videoId"
              formGroupProps={{ label: 'Video ID', helperText: 'The ID of the video in either Youtube or Vimeo' }}
            />
            <RadioSelectField
              name="provider"
              inline={true}
              options={[
                { label: 'Youtube', value: 'youtube' },
                { label: 'Vimeo', value: 'vimeo' },
              ]}
              formGroupProps={{ label: 'Provider' }}
            />
            <SubmitButton text="Add Video" fill={true} />
          </>
        </Formik>
      </Dialog>
    </>
  );
}

interface ICharacterCounter {
  displayThreshold: number;
  limit: number;
  name: string;
  style?: React.CSSProperties;
}

function CharacterCounter({ displayThreshold, name, limit, style }: ICharacterCounter) {
  const [field] = useField<string>(name);
  const length = field?.value?.length || 0;
  const intent = length > limit ? Intent.DANGER : Intent.NONE;

  if (length < displayThreshold) return null;

  return (
    <Caption {...{ intent, style }}>
      {length} / {limit} characters
    </Caption>
  );
}
